import { Injectable } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client/core';
import {
  getDefaultEntityListStateWithoutFilter,
  getDefaultPaginatedEntityListStateWithDateFilter,
  getDefaultPaginatedEntityListStateWithoutFilter,
} from '@core/helper/state-helper';
import {
  BasePaginatedEntityListStateWithDateFilter,
  BasePaginatedEntityListStateWithoutFilter,
} from '@core/interfaces/base-entity-list-state.interface';
import { ChangePerformerFilter, OrderFilterDto } from '@core/interfaces/change-perform.interface';
import { Order } from '@core/interfaces/order-reference-guide.interface';
import { RouteSheetList, RouteSheetListGql, RouteSheetsFilter } from '@core/interfaces/route-sheets.interface';
import { ChangePerformerService } from '@core/services/change-performer.service';
import {
  Action, Selector, State, StateContext,
} from '@ngxs/store';
import { Observable, tap } from 'rxjs';
import {
  ChangePerformerSetCity,
  ChangePerformerSetZoneId,
  GetListOrdersChengePedrformer,
  GetListRouteSheetsWithoutRouteAndOrder,
  ResetChangePerformerFilter,
  SetChangePerformerFilter,
} from '../action/change-performer.action';

type ChangePerformerRouteSheetsState = BasePaginatedEntityListStateWithDateFilter<RouteSheetList, RouteSheetsFilter>;

export interface ChangePerformerStateModel {
  changePerformerFilter: ChangePerformerFilter | null;
  routeSheet: RouteSheetList | null;
  zoneId: number | null;
  cityId: number | null;
  routeSheets: ChangePerformerRouteSheetsState;
  orders: Array<Order>;
}

@State<ChangePerformerStateModel>({
  name: 'ChangePerformerState',
  defaults: {
    changePerformerFilter: null,
    routeSheet: null,
    routeSheets: getDefaultPaginatedEntityListStateWithDateFilter<RouteSheetsFilter, RouteSheetList>(),
    orders: [],
    zoneId: null,
    cityId: null,
  },
})
@Injectable()
export class ChangePerformerState {
  constructor(private readonly changePerformerService: ChangePerformerService) {}

  @Selector()
  public static getChangePerformerFilter(state: ChangePerformerStateModel): ChangePerformerFilter | null {
    return state.changePerformerFilter;
  }

  @Selector()
  public static getRouteSheetsWithoutOrder(state: ChangePerformerStateModel): RouteSheetList[] {
    return state.routeSheets.list;
  }

  @Selector()
  public static getOrders(state: ChangePerformerStateModel): Order[] {
    return state.orders;
  }

  @Selector()
  public static getZoneId(state: ChangePerformerStateModel): number | null {
    return state.zoneId;
  }

  @Selector()
  public static getCityId(state: ChangePerformerStateModel): number | null {
    return state.cityId;
  }

  @Action(SetChangePerformerFilter)
  public setChangePerformerFilter(
    ctx: StateContext<ChangePerformerStateModel>,
    { params }: SetChangePerformerFilter,
  ): void {
    ctx.patchState({ changePerformerFilter: params });
  }

  @Action(ResetChangePerformerFilter)
  public resetChangePerformerFilter(ctx: StateContext<ChangePerformerStateModel>): void {
    ctx.patchState({
      changePerformerFilter: null,
      orders: [],
      zoneId: null,
      cityId: null,
    });
  }

  @Action(ChangePerformerSetZoneId)
  public setZoneId(ctx: StateContext<ChangePerformerStateModel>, { zoneId }: ChangePerformerSetZoneId): void {
    ctx.patchState({
      zoneId,
    });
  }

  @Action(ChangePerformerSetCity)
  public setCity(ctx: StateContext<ChangePerformerStateModel>, { cityId }: ChangePerformerSetCity): void {
    ctx.patchState({
      cityId,
    });
  }

  @Action(GetListOrdersChengePedrformer)
  public getListOrders(
    ctx: StateContext<ChangePerformerStateModel>,
    { params, pagination }: GetListOrdersChengePedrformer,
  ): Observable<ApolloQueryResult<any>> {
    return this.changePerformerService.getListOrder(params, pagination).pipe(
      tap((res) => {
        if (res?.data) {
          ctx.patchState({
            orders: res.data.getListOrder.result,
          });
        }
      }),
    );
  }

  @Action(GetListRouteSheetsWithoutRouteAndOrder)
  public getListRouteSheets(
    ctx: StateContext<ChangePerformerStateModel>,
    { params }: GetListRouteSheetsWithoutRouteAndOrder,
  ): Observable<ApolloQueryResult<RouteSheetListGql>> {
    return this.changePerformerService.getRouteListWithoutRouteAndOrder(params).pipe(
      tap((res) => {
        if (res?.data) {
          ctx.patchState({
            routeSheets: {
              ...ctx.getState().routeSheets,
              list: res.data.getListRouteList.result,
              total: res.data.getListRouteList.total,
            },
          });
        }
      }),
    );
  }
}
